import { Injectable, computed, effect, inject } from '@angular/core';
import { AuthStore, SyncedCollection } from '@huddle-sports-platform/app-core';
import {
  acceptsCurrentAdminTerms,
  clubSchema,
  type Club,
} from '@huddle-sports-platform/schema';
import { clubPaymentsVerification } from '@huddle-sports-platform/schema/client';
import { collection, documentId, query, where } from 'firebase/firestore';

import { db } from '../core/firebase';
import { refreshClubPaymentsStatus } from './callables';
import { ClubBrandStore } from './club-brand';

/**
 * The club doc, LIVE, for EVERY signed-in user - and from it the one
 * question the routing keeps asking: where does this club stand with its
 * payment provider? (docs/club-onboarding.md.)
 *
 * The `payments` mirror on the club doc is server-written only; the kit's
 * `clubPaymentsVerification()` turns it into
 * `not_started | pending | action_needed | verified`, and that single
 * derivation drives every surface that asks: the admin Finance checklist,
 * the admin-area status banner, the member payment surfaces and HQ's
 * card.
 *
 * Loaded by data-init for everyone. A BRAND-NEW sign-up is not a club
 * user yet and cannot read the doc - the loader settles on the failure
 * (fail open, verification() null gates nothing) and {@link whenLoaded}
 * retries on the next ask, once `onboardedGuard`'s auto-join has run.
 */
@Injectable({ providedIn: 'root' })
export class ClubPaymentsStore {
  private readonly auth = inject(AuthStore);
  private readonly clubBrand = inject(ClubBrandStore);

  private readonly clubSync = new SyncedCollection<Club>(clubSchema, 'club-payments');

  /** The club doc, live; undefined until the first snapshot lands. */
  readonly club = computed<Club | undefined>(() => [...this.clubSync.docs().values()][0]);

  /** Payment verification status, or null before the doc has answered -
   *  and null gates NOTHING (the rules are the boundary, not this). */
  readonly verification = computed(() => {
    const club = this.club();
    return club ? clubPaymentsVerification(club) : null;
  });

  /** What the club has accepted of Huddle's administrator terms - absent
   *  until it has (server-written; the acceptAdminTerms Callable). */
  readonly terms = computed(() => this.club()?.terms);

  /** Has the club accepted the version currently IN FORCE? The bank flow
   *  asks this, and so does the Home checklist. Deliberately not "has it
   *  accepted anything": an older acceptance is a record, not a licence. */
  readonly termsAccepted = computed(() => acceptsCurrentAdminTerms(this.terms()));

  /** The pair the CURRENT successful load answered for (`uid|clubId`).
   *  Deliberately NOT set on failure - a fresh sign-up that could not read
   *  the club doc yet gets a clean retry after the auto-join. */
  private loadedFor: string | undefined;
  private inflight: Promise<void> | null = null;

  constructor() {
    // Sign-out resets the cycle; the next account decides for itself.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      if (uid === null) {
        this.loadedFor = undefined;
        this.clubSync.detach();
      }
    });
    // LOAD WHEN THE PAIR IS KNOWN. Data-init's settle can run before the
    // whitelabel brand resolves, in which case whenLoaded() returned
    // without loading and every consumer read null forever - the bank
    // card showed its connect face on a verified club, and the pages
    // that gate on the answer gated nothing. The store owns its own
    // retry now, so no consumer has to remember to ask.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      const clubId = this.clubBrand.brand()?.clubId ?? null;
      if (uid && clubId) void this.whenLoaded();
    });
  }

  /**
   * Resolves once the club doc has answered for the CURRENT uid+club -
   * first snapshot in, or the read failed (settled either way; a loader
   * must never hang the boot veil). Imperative and idempotent; instant
   * when there is no branded club (dev shared app - nothing to gate).
   */
  async whenLoaded(): Promise<void> {
    const uid = this.auth.user()?.uid ?? null;
    const clubId = this.clubBrand.brand()?.clubId ?? null;
    if (!uid || !clubId) return;
    const key = `${uid}|${clubId}`;
    if (this.loadedFor === key) return;
    if (this.inflight) return this.inflight;
    this.inflight = this.load(clubId)
      .then((ok) => {
        if (ok) this.loadedFor = key;
      })
      .finally(() => {
        this.inflight = null;
      });
    return this.inflight;
  }

  /** Pull the latest provider status onto the club-doc mirror, which the
   *  live listener then delivers - so the checklist and banner never
   *  describe yesterday. Settles quietly when there is nothing to pull
   *  (no account yet); the webhook remains the fallback truth. */
  async refreshFromProvider(): Promise<void> {
    const clubId = this.clubBrand.brand()?.clubId;
    if (!clubId) return;
    try {
      await refreshClubPaymentsStatus({ clubId });
    } catch {
      // failed-precondition = payments never set up; transport trouble =
      // try again next open. Either way the mirror's last word stands.
    }
  }

  private async load(clubId: string): Promise<boolean> {
    this.clubSync.warm(query(collection(db(), 'clubs'), where(documentId(), '==', clubId)));
    for (;;) {
      if (this.clubSync.failed()) return false;
      if (this.clubSync.attached() && !this.clubSync.warming()) return true;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }
}
